/**
 * Creatives backend. Same two services and the same run/poll/refetch shape as the logo
 * pipeline (`../casino-settings/logo-api`), pointed at the brand's in-website banners
 * instead of its logos.
 *
 * A creative edit is a pipeline run, not a save:
 *
 *   1. (optional) {@link uploadArtwork} — spark-ai stores an owner's own image, returns a CDN URL.
 *   2. {@link queueCreativeRun}         — casino-creation queues a run that (re)generates a
 *                                          placement's banner, PREVIEW-first so nothing on the
 *                                          live site changes until it is applied.
 *   3. {@link fetchCreativeRunStatus}   — poll until the run is over; a preview run returns the
 *                                          pending artwork URLs.
 *   4. {@link applyCreativePreview}     — commit the pending artwork to the live site, or
 *      {@link setCreativeUrl}              write an owner-supplied image straight in.
 *
 * WHY THE PROXY / SCOPES: identical to the logo pipeline. Both services are reached through
 * the owner-hub MLP proxy so a LAUNCHED brand authenticates with the owner's own token (not a
 * terminal deploy session). spark-ai is general-scope (`g`); casino-creation is cluster-scope
 * (`c`) and every call carries the brand `tenant_id`.
 *
 * BUILD-AHEAD: the `banner-family` read + preview/apply/urls routes are the contract the
 * backend does not ship yet (see the Creative Kit brief, "Contract B"). They mirror the logo
 * family, which already has preview→apply (`logo-family/apply`) and a direct URL write
 * (`logo-family/urls`), so these are analogues, not inventions. Until they exist the reads
 * fail and the page keeps an honest empty/loading state.
 */
import axios, { type AxiosRequestConfig } from 'axios';

import { createMlpProxyTransport } from '../../utils/mlp-proxy';
import {
  fetchLogoFamily,
  fetchRunStatus as fetchLogoRunStatus,
  queueLogoRun,
  uploadArtwork as uploadLogoArtwork,
  type LogoFamily,
} from '../casino-settings/logo-api';

/* ── transports (identical config to logo-api) ──────────────────────────────── */
const art = createMlpProxyTransport({
  svc: 'api',
  scope: 'g',
  pathPrefix: '/api/v1',
  headers: { Accept: 'application/json' },
});

export const casinoCreation = createMlpProxyTransport({
  svc: 'cc',
  scope: 'c',
  headers: { Accept: 'application/json' },
});

/** True when the owner-hub proxy base is configured — nothing below can work without it. */
export const creativeApiConfigured = (): boolean => art.configured() && casinoCreation.configured();

/* ── domain types ───────────────────────────────────────────────────────────── */

/** Who authored a creative. `lumio` = AI-generated; `custom` = the owner's own artwork. */
export type CreativeOrigin = 'lumio' | 'custom' | string;

/** A curated site carousel — an ordered selection of pool banners the owner picks for a page. Homepage,
 *  Casino and Sports each render their own carousel; the banners themselves live in the pool
 *  ("all Promotions"). Curation (which banners, in what order) is separate from publishing to the pool. */
export type CreativeCarousel = 'homepage' | 'casino' | 'sports';

/** The curated carousels, in display order. */
export const CREATIVE_CAROUSELS: CreativeCarousel[] = ['homepage', 'casino', 'sports'];

/** One artwork in a banner's version history — the initial banner plus each regeneration. */
export interface CreativeVersion {
  /** Version id: a live banner id (the initial) or a stored creative id (a regeneration). */
  id: string;
  image_url?: string;
  origin?: CreativeOrigin;
  /** The brief this version was generated from (empty for the initial live banner). */
  prompt?: string;
  /** Reference images the owner attached to guide this regeneration (CDN urls), so the editor can
   *  show how the result was reached. Empty for the initial banner / prompt-only regenerations. */
  references?: string[];
  /** The version this one was regenerated FROM (its parent's id, resolvable in the same family), so
   *  the editor can show the source artwork alongside the brief. Empty for the initial banner. */
  parent_id?: string;
  /** Every distinct SLOT of the banner (aspect ratio × key position, e.g. `ar21_keyright`) mapped to its
   *  drawn image — matching the backend's `wlplatform.Slot`, NOT a bare aspect ratio. The full set the owner
   *  can view/download; what publishing writes verbatim to the live banner. A main-only version (fresh
   *  "Generate new") holds just its main until its formats are generated on demand. */
  formats?: Record<string, string>;
  /** The banner's full slot count this version is meant to have, and whether it has them all.
   *  An incomplete version must have its formats generated (charged) before it can be published. */
  total_formats?: number;
  formats_complete?: boolean;
  created_at?: string;
  /** What a generated version was drawn with: the brand's art style and the mascot it featured, as named at draw
   *  time. Absent when no art style was active. `mascot_pick` is how the mascot was chosen: "" (auto), "none" or
   *  the picked mascot's id. */
  art_style?: {
    style_id?: string;
    style_name?: string;
    mascot_id?: string;
    mascot_name?: string;
    mascot_avatar?: string;
    mascot_pick?: string;
  };
  /** True for the first version (the original live banner / first creative). */
  is_initial?: boolean;
  /** True for a soft-deleted version: it's kept for the editor's "Archived" section (recoverable), and
   *  excluded from the Versions list + the card's stack/count. */
  archived?: boolean;
}

/** One creative in a placement — what the site renders in that slot. */
export interface CreativeItem {
  banner_id: string;
  image_url?: string;
  /** CraftJS layout id of the banner template, e.g. `betsson-01`. */
  layout?: string;
  origin?: CreativeOrigin;
  is_active?: boolean;
  /** Internal name from the banner+promotion pair (diagnostics / a11y label fallback). */
  internal_name?: string;
  /** The banner's on-site copy + context, so the card describes where it runs and what it says. */
  headline?: string; // primary overlay line
  subtitle?: string; // secondary overlay line
  context?: string; // where it runs, e.g. "CASINO"
  cta_text?: string; // the button label
  cta_href?: string; // where it links, e.g. "/deposit"
  /** Version history, oldest→newest (initial + regenerations). The card shows the published version
   *  (else the initial) and stacks when there is more than one; `published_version_id` marks which version
   *  is live on the site (empty = still the initial banner). */
  versions?: CreativeVersion[];
  version_count?: number;
  published_version_id?: string;
  /** True when the family has a generated version the owner hasn't seen yet (a finished
   *  regeneration they haven't opened) — the card flags it "new". Cleared by opening the editor. */
  has_new?: boolean;
  /** Number of DISTINCT aspect ratios in the banner — the number of draws a regeneration makes, so
   *  the editor can show its true credit cost (per-draw × this) up front. This includes the paired
   *  market-offer's bonus slots (bonus_list / bonus_selection) when the banner has one, since those
   *  are drawn + charged alongside the banner's own formats. */
  format_count?: number;
  /** The live banner's platform ordering weight (higher = more prominent). Used to SEED an empty
   *  carousel's curation with a priority-ordered default; manual curation then overrides it. */
  priority?: number;
}

/** A named site placement (data source), e.g. `spark-main-banners` (the hero carousel). */
export interface CreativePlacement {
  placement: string;
  /** Human label if the backend provides one; the UI humanises the id otherwise. */
  label?: string;
  items: CreativeItem[];
}

/** `GET /casino/tenant/{id}/banner-family` — every in-website creative, grouped by placement. */
export interface CreativeFamily {
  tenant_id: number;
  placements: CreativePlacement[];
  /**
   * Which real asset family backs this view. `logos` = the brand's live logo family (owner-
   * accessible today, edited through the proven logo pipeline). `banners` = the hero/promo
   * banner family (build-ahead). The edit surface adapts to the source.
   */
  source?: 'logos' | 'banners';
}

/** The hero-carousel placement id (its casino-creation selector is a named one, below). */
export const HERO_PLACEMENT = 'spark-main-banners';

/* ── 1. read the family back ────────────────────────────────────────────────── */

/** The brand's live in-website creatives, straight from casino-creation. */
export const fetchCreativeFamily = async (tenantId: number, signal?: AbortSignal): Promise<CreativeFamily> => {
  const response = await axios.get<CreativeFamily>(
    casinoCreation.url(`/casino/tenant/${tenantId}/banner-family`, tenantId),
    casinoCreation.requestConfig(signal),
  );
  return response.data;
};

/* ── 2. upload the owner's own artwork (spark-ai, reused verbatim from the logo flow) ── */

type UploadResponse = { success?: boolean; image?: { cdn_url?: string; width?: number; height?: number } };

/**
 * Store an owner's picked image and return its permanent CDN URL. Same spark-ai endpoint the
 * logo flow uses (`/art/logo/upload`) — it stores any image and rasterises SVG to PNG; it is
 * not logo-specific. The stored URL is then placed via {@link setCreativeUrl} or fed to a run.
 */
export const uploadArtwork = async (imageDataUri: string, brandName: string, signal?: AbortSignal): Promise<string> => {
  const response = await axios.post<UploadResponse>(
    art.url('/art/logo/upload'),
    { image_base64: imageDataUri, brand_name: brandName },
    art.requestConfig(signal),
  );
  const cdnUrl = response.data?.image?.cdn_url;
  if (!cdnUrl) throw new Error('The upload did not return a stored image');
  return cdnUrl;
};

/** A stored image with the dimensions spark-ai measured (0 when it did not report them). */
export interface UploadedArtwork {
  url: string;
  width: number;
  height: number;
}

/**
 * {@link uploadArtwork}, keeping the dimensions the upload answer carries (the SEO share image needs them). Same
 * endpoint, same body.
 */
export const uploadArtworkRef = async (
  imageDataUri: string,
  brandName: string,
  signal?: AbortSignal,
): Promise<UploadedArtwork> => {
  const response = await axios.post<UploadResponse>(
    art.url('/art/logo/upload'),
    { image_base64: imageDataUri, brand_name: brandName },
    art.requestConfig(signal),
  );
  const image = response.data?.image;
  const cdnUrl = image?.cdn_url;
  if (!cdnUrl) throw new Error('The upload did not return a stored image');
  return { url: cdnUrl, width: image.width ?? 0, height: image.height ?? 0 };
};

/* ── 3. queue a run ─────────────────────────────────────────────────────────── */

/** What kind of edit a run performs — decides the selector and which inputs it carries. */
export type CreativeRunMode = 'generate' | 'restyle';

export interface QueueCreativeRunOptions {
  tenantId: number;
  /** Placement being edited, e.g. `spark-main-banners`. */
  placement: string;
  mode: CreativeRunMode;
  /**
   * Preview-first: generate into a pending state and return preview URLs WITHOUT touching the
   * live site (default true). Applying is a separate, explicit step. Mirrors the logo flow's
   * `preview` → `logo_pending` → apply.
   */
  preview?: boolean;
  /** A natural-language brief for a `generate` run ("Restyle the hero for Día de Muertos"). */
  prompt?: string;
  /** An owner-uploaded image (CDN URL from {@link uploadArtwork}) to seed the placement with. */
  uploadUrl?: string;
  /** A `restyle` run swaps the banner look to this set (the art style's banner-set id). */
  bannerSetId?: string;
  /** Force a fresh bundle rather than a cached one. */
  forceRegen?: boolean;
}

export interface QueuedCreativeRun {
  pipelineId: string;
  tenantId: number;
  placement: string;
  selector: string;
}

/** casino-creation selector for a placement. The hero has its own named selector; every other
 *  placement is addressed as `banner_family:<placement>`. */
export const selectorForPlacement = (placement: string): string =>
  placement === HERO_PLACEMENT ? 'above_the_fold_hero' : `banner_family:${placement}`;

/**
 * Ask casino-creation to (re)generate a placement's creative. Preview-first by default, so the
 * live site is untouched until {@link applyCreativePreview}. Returns as soon as the run is
 * QUEUED — the artwork does not exist yet; poll {@link fetchCreativeRunStatus}.
 */
export const queueCreativeRun = async (
  { tenantId, placement, mode, preview = true, prompt, uploadUrl, bannerSetId, forceRegen }: QueueCreativeRunOptions,
  signal?: AbortSignal,
): Promise<QueuedCreativeRun> => {
  const selector = selectorForPlacement(placement);
  const response = await axios.post<{ pipeline_id?: string }>(
    casinoCreation.url('/casino/run', tenantId),
    {
      tenantId,
      mode: 'redeploy',
      selector,
      preview,
      ...(prompt ? { prompt } : {}),
      ...(uploadUrl ? { uploadUrl } : {}),
      ...(mode === 'restyle' && bannerSetId ? { siteArtStyleBannerSetId: bannerSetId } : {}),
      ...(forceRegen ? { forceSystemArtRegeneration: true } : {}),
    },
    casinoCreation.requestConfig(signal),
  );
  const pipelineId = response.data?.pipeline_id;
  if (!pipelineId) throw new Error('The run was not queued');
  return { pipelineId, tenantId, placement, selector };
};

/* ── 3b. follow the run ─────────────────────────────────────────────────────── */

const TERMINAL_RUN_STATUSES = new Set(['completed', 'failed', 'cancelled']);

export interface CreativeRunStatus {
  status: string;
  settled: boolean;
  failed: boolean;
  error?: string;
  /** Preview artwork produced by a preview run, once it has settled (placement/banner → URL). */
  previewUrls?: string[];
}

/** Where one queued creative run has got to. A settled preview run carries `previewUrls`. */
export const fetchCreativeRunStatus = async (
  pipelineId: string,
  tenantId: number,
  signal?: AbortSignal,
): Promise<CreativeRunStatus> => {
  const response = await axios.get<{ status?: string; error?: string; preview_urls?: string[] }>(
    casinoCreation.url(`/casino/status/${pipelineId}`, tenantId),
    casinoCreation.requestConfig(signal),
  );
  const status = response.data?.status ?? 'unknown';
  return {
    status,
    settled: TERMINAL_RUN_STATUSES.has(status),
    failed: status === 'failed' || status === 'cancelled',
    error: response.data?.error,
    previewUrls: Array.isArray(response.data?.preview_urls) ? response.data?.preview_urls : undefined,
  };
};

/* ── 4. commit / direct write ───────────────────────────────────────────────── */

/** Commit a placement's pending preview to the live site (analogue of `logo-family/apply`). */
export const applyCreativePreview = async (
  tenantId: number,
  placement: string,
  signal?: AbortSignal,
): Promise<void> => {
  await axios.post(
    casinoCreation.url(`/casino/tenant/${tenantId}/banner-family/apply`, tenantId),
    { placement },
    casinoCreation.requestConfig(signal),
  );
};

/** Write an owner-supplied image straight into a placement, no regeneration (analogue of
 *  `logo-family/urls`). Used by the "use my own artwork" path once the file is uploaded. */
export const setCreativeUrl = async (
  tenantId: number,
  input: { placement: string; banner_id?: string; image_url: string },
  signal?: AbortSignal,
): Promise<void> => {
  await axios.post(
    casinoCreation.url(`/casino/tenant/${tenantId}/banner-family/urls`, tenantId),
    input,
    casinoCreation.requestConfig(signal),
  );
};

/* ── 5. banner looks (restyle picker) ───────────────────────────────────────── */

/** One selectable banner look for a restyle — a banner set from the brand's art style. */
export interface BannerLook {
  id: string;
  name: string;
  /** A representative thumbnail, when the catalogue provides one. */
  thumbnail_url?: string;
}

/**
 * The banner looks the brand can restyle to — the banner sets compatible with its art style.
 *
 * BUILD-AHEAD: the `/casino/tenant/{id}/banner-looks` endpoint is not deployed yet (it 404s), so
 * we do NOT call it — the restyle picker shows its empty state until the backend ships. When it
 * does, restore the fetch below:
 *
 *   const response = await axios.get<{ looks?: BannerLook[] } | BannerLook[]>(
 *     casinoCreation.url(`/casino/tenant/${tenantId}/banner-looks`, tenantId),
 *     casinoCreation.requestConfig(signal),
 *   );
 *   const data = response.data as { looks?: BannerLook[] } | BannerLook[] | undefined;
 *   return Array.isArray(data) ? data : Array.isArray(data?.looks) ? data.looks : [];
 */
export const fetchBannerLooks = async (_tenantId: number, _signal?: AbortSignal): Promise<BannerLook[]> => {
  return [];
};

/* ── spark-creatives service: the real creatives read ────────────────────────
 *
 * The dedicated spark-creatives backend (`window.config.creativesApiUrl`) returns the
 * brand's creatives already grouped into the placements/items shape below: the live
 * in-website banners it reads server-side (the read the browser can't do, since the
 * data-source guest token is server-side) plus anything generated. This is the SOLE read for
 * the Creatives page: when the service is unconfigured or unreachable the page shows its empty
 * state (see `loadBannerFamily` / `useCreatives`) rather than falling back to the logo family,
 * which would masquerade the brand's logo slots as banners. The logo-family read further down
 * now backs only the separate logo-replacement pipeline, not a page-load fallback. */

/** The spark-creatives service base (`…/api/spark-creatives/v1`), or '' when unset. */
export const creativesServiceBase = (): string => {
  const url = (window.config as unknown as Record<string, unknown> | undefined)?.creativesApiUrl;
  return typeof url === 'string' ? url.replace(/\/+$/, '') : '';
};

/** True when the spark-creatives service URL is configured. */
export const creativesServiceConfigured = (): boolean => creativesServiceBase() !== '';

/** Shared request config for calls to the spark-creatives service: the raw stored access token AS
 *  the `Authorization` header value (no `Bearer ` prefix, the service expects it raw, like every
 *  call below), `_owsNoAuthRecovery: true` so a non-2xx from this SEPARATE service never rotates
 *  the owner-hub token, and by default `validateStatus` treats every status as an answer to parse
 *  rather than a thrown error (pass a narrower one, e.g. 2xx-only, to fall back to axios throwing).
 *  Consolidates the block inlined across the calls above. Only the tenant-source module
 *  (`./creatives-tenant-source`) consumes it so far; the existing call sites are left as is. */
export const creativesRequestConfig = (signal?: AbortSignal, validateStatus?: (status: number) => boolean): AxiosRequestConfig => {
  const token = localStorage.getItem('access_token') || '';
  return {
    signal,
    _owsNoAuthRecovery: true,
    validateStatus: validateStatus ?? (() => true),
    ...(token ? { headers: { Authorization: token } } : {}),
  };
};

/** One placement's items as the service returns them (already the CreativeItem shape). */
interface ServicePlacement {
  placement: string;
  label?: string;
  items?: CreativeItem[];
}

/**
 * Read the brand's creatives from the spark-creatives service. The service already returns
 * the placements grouped (the live in-website banners it fetched with the brand's guest
 * token, plus generated creatives), so this maps the response straight onto CreativeFamily.
 * `source: 'banners'` so the page offers the AI edit modes.
 */
export const fetchCreativeFamilyFromService = async (
  tenantId: number,
  domain?: string,
  signal?: AbortSignal,
): Promise<CreativeFamily> => {
  const base = creativesServiceBase();
  const token = localStorage.getItem('access_token') || '';
  // A 401 from this separate service must never rotate the owner-hub token.
  const config: AxiosRequestConfig = {
    signal,
    _owsNoAuthRecovery: true,
    ...(token ? { headers: { Authorization: token } } : {}),
  };
  // Pass the brand's own domain so the service can resolve its live guest token dynamically
  // (no baked-in tokens); the service falls back to a seeded token when it can't.
  const query = domain ? `?domain=${encodeURIComponent(domain)}` : '';
  const response = await axios.get<{ tenant_id?: string | number; placements?: ServicePlacement[] }>(
    `${base}/creatives/tenant/${encodeURIComponent(String(tenantId))}${query}`,
    config,
  );

  const data = response.data ?? {};
  const placements: CreativePlacement[] = (data.placements ?? []).map((p) => ({
    placement: p.placement,
    label: p.label,
    items: (p.items ?? []).map((item) => ({
      banner_id: item.banner_id,
      image_url: item.image_url,
      layout: item.layout,
      origin: item.origin,
      internal_name: item.internal_name,
      headline: item.headline,
      subtitle: item.subtitle,
      context: item.context,
      cta_text: item.cta_text,
      cta_href: item.cta_href,
      is_active: item.is_active,
      versions: item.versions,
      version_count: item.version_count,
      published_version_id: item.published_version_id,
      has_new: item.has_new,
      format_count: item.format_count,
      priority: item.priority,
    })),
  }));
  return { tenant_id: Number(data.tenant_id) || tenantId, placements, source: 'banners' };
};

/** Result of a publish enqueue — `ok:false` with `needed`/`balance` is the 402 insufficient-credits answer. */
export interface PublishResult {
  ok: boolean;
  needed?: number;
  balance?: number;
}

/**
 * Publish a version to the brand's LIVE site — the pool (the /promotions page). Enqueues a job that
 * rewrites the pool record's art-bundle image URLs (regenerating the square/portrait orientations to
 * match the published landscape), so the customer site shows it. Progress is tracked by the durable jobs
 * tray. Returns `{ ok:false, needed, balance }` on a 402 so the caller can open Buy-credits. Direct
 * service call, like {@link setCurrentBannerVersion}. Which pages a banner appears on (Homepage / Casino
 * / Sports) is a separate curation step (see {@link setCarousel}).
 */
export const publishBanner = async (
  tenantId: number,
  rootBannerId: string,
  versionId: string | undefined,
  signal?: AbortSignal,
): Promise<PublishResult> => {
  const base = creativesServiceBase();
  if (!base) return { ok: false };
  const token = localStorage.getItem('access_token') || '';
  const config: AxiosRequestConfig = {
    signal,
    _owsNoAuthRecovery: true,
    validateStatus: (s) => (s >= 200 && s < 300) || s === 402,
    ...(token ? { headers: { Authorization: token } } : {}),
  };
  const response = await axios.post<{ ok?: boolean; needed?: number; balance?: number }>(
    `${base}/creatives/tenant/${encodeURIComponent(String(tenantId))}/banner/${encodeURIComponent(rootBannerId)}/publish`,
    { ...(versionId ? { version_id: versionId } : {}) },
    config,
  );
  if (response.status === 402) {
    return { ok: false, needed: response.data?.needed, balance: response.data?.balance };
  }
  return { ok: response.data?.ok !== false };
};

/** Mark a banner family's regenerations SEEN, clearing the card's "new" cue. Fire-and-forget when
 *  the owner opens the editor. Direct service call, like {@link publishBanner}. */
export const markBannerSeen = async (tenantId: number, rootBannerId: string, signal?: AbortSignal): Promise<void> => {
  const base = creativesServiceBase();
  if (!base) return;
  const token = localStorage.getItem('access_token') || '';
  const config: AxiosRequestConfig = {
    signal,
    _owsNoAuthRecovery: true,
    ...(token ? { headers: { Authorization: token } } : {}),
  };
  await axios.post(
    `${base}/creatives/tenant/${encodeURIComponent(String(tenantId))}/banner/${encodeURIComponent(rootBannerId)}/seen`,
    {},
    config,
  );
};

/** A site data-source payload for the framed-site preview — exactly what the casino site drops into its
 *  `dataSources.items[dsId]` to render the unpublished draft order. `source` is the site's own record
 *  shape (opaque here); we only relay it. */
export interface PreviewSource {
  ds_id: string;
  source: { element_type?: { type_id?: string }; meta?: unknown; data?: unknown[] };
}

/** One banner record inside a preview source (opaque site shape; we only touch its id + art bundle). */
type PreviewBannerRecord = {
  id?: string;
  banner_id?: string;
  art_bundle?: { data?: Record<string, string> } & Record<string, unknown>;
} & Record<string, unknown>;

/** Map a record's art-variant key to the version's SLOT key — `<arWH>_<keyPOS>`, e.g.
 *  `landscape_ar21_keyright` → `ar21_keyright`, `small_square_ar11_keycenter` → `ar11_keycenter`. This
 *  mirrors the backend's `wlplatform.Slot`: publishing writes `version.formats[slot]` into each variant. */
export const slotOfVariant = (variantKey: string): string => {
  const parts = variantKey.toLowerCase().split('_');
  const aspect = parts.find((p) => /^ar\d+$/.test(p)) ?? '';
  // Require a character after "key" (keycenter/keyright/…), matching the backend's position rule exactly,
  // so a bare "key" token can never be mistaken for a position.
  const pos = parts.find((p) => /^key.+/.test(p)) ?? '';
  return aspect ? (pos ? `${aspect}_${pos}` : aspect) : '';
};

/** The formats (slot → image url) of a creative item's PUBLISHED art: the version live on the site
 *  (`published_version_id`, else the initial live banner), the same rule the service uses for the card face.
 *  Never a newer unpublished regeneration from the versions workspace. Empty when that version carries no
 *  formats, or the item has no live banner at all (a game/feature subject, a generated-only family), so the
 *  caller falls back to the item's card-face `image_url`. */
const publishedVersionFormats = (item: CreativeItem): Record<string, string> => {
  const pub = item.published_version_id;
  return item.versions?.find((v) => (pub ? v.id === pub : v.is_initial))?.formats ?? {};
};

/** A promotion slot's aspect (w/h) parsed from its key, e.g. `ar12_keycenter` → 0.5, `ar21_keyright` → 2.
 *  Undefined for an unparseable key. Single-digit W and H, matching the banner format set (1:1, 1:2, 2:1, …). */
export const slotAspect = (slot: string): number | undefined => {
  const m = /^ar(\d)(\d)(?:_key.+)?$/.exec(slot.toLowerCase());
  if (!m) return undefined;
  const w = Number(m[1]);
  const h = Number(m[2]);
  return h > 0 ? w / h : undefined;
};

/**
 * Pick a promotion's PUBLISHED key-art format whose aspect is CLOSEST to `targetAspect` (w/h), preferring a centered crop
 * on ties. Used to seed a video's start frame from the right-shaped art instead of always the 2:1 hero — e.g. a
 * 9:16 video gets the 1:2 (portrait) format, which the pipeline then cover-crops to the exact target, so the
 * branding survives instead of being reframed away. Returns undefined when the item has no usable formats, so
 * the caller falls back to the item's primary `image_url` (today's behaviour).
 */
export const bestFormatUrlForAspect = (item: CreativeItem, targetAspect: number): string | undefined => {
  const formats = publishedVersionFormats(item);
  let best: { url: string; diff: number; center: boolean } | undefined;
  for (const [slot, url] of Object.entries(formats)) {
    if (!url) continue;
    const a = slotAspect(slot);
    if (a === undefined) continue;
    const diff = Math.abs(a - targetAspect);
    const center = slot.toLowerCase().includes('keycenter');
    const better =
      !best || diff < best.diff - 1e-9 || (Math.abs(diff - best.diff) < 1e-9 && center && !best.center);
    if (better) best = { url, diff, center };
  }
  return best?.url;
};

/**
 * Build a preview source with ONE banner's art swapped to a just-published version's art, so the live
 * preview can show it INSTANTLY (optimistically) without waiting ~5s for the backend to reflect the publish.
 *
 * A version carries its art as {@link CreativeVersion.formats}, keyed by SLOT (`<arWH>_<keyPOS>`, e.g.
 * `ar21_keyright`), plus a primary `image_url`. A site banner record carries art as `art_bundle.data` keyed
 * by variant (`landscape_ar21_keyright`, `square_ar11_keycenter`, ...). We fill each variant the same way the
 * backend does on publish (`RewriteArtBundleImagesBySlot`): `data[K] = formats[slot(K)]` — exact key first,
 * then the slot, then a bare-aspect fallback (older payloads), and for a formats-less version at least the
 * hero variant gets `image_url`. Any variant the version doesn't cover is LEFT on its old art (exactly what
 * the live site keeps), so once the backend catches up its art is byte-identical and reconciliation is a
 * no-op. Returns the source unchanged when the banner or its art bundle isn't present.
 */
export const applyVersionArtToSource = (
  source: unknown,
  bannerId: string,
  version: { formats?: Record<string, string>; image_url?: string },
): unknown => {
  const src = source as PreviewSource['source'];
  const data = Array.isArray(src?.data) ? (src.data as PreviewBannerRecord[]) : null;
  if (!data) return source;
  const idx = data.findIndex((r) => (r?.id ?? r?.banner_id) === bannerId);
  if (idx < 0) return source;
  const rec = data[idx];
  const bundle = rec?.art_bundle?.data;
  if (!bundle || typeof bundle !== 'object') return source;
  const formats = version.formats ?? {};
  const nextBundle: Record<string, string> = {};
  for (const [variantKey, oldUrl] of Object.entries(bundle)) {
    const lower = variantKey.toLowerCase();
    const aspect = lower.match(/ar\d+/)?.[0];
    const isHero = lower.startsWith('landscape_ar21_key');
    nextBundle[variantKey] =
      formats[variantKey] ?? // exact full variant key (defensive)
      formats[slotOfVariant(variantKey)] ?? // PRIMARY: the backend slot (aspect_position)
      (aspect ? formats[aspect] : undefined) ?? // back-compat with older bare-aspect payloads
      (isHero ? version.image_url : undefined) ?? // formats-less version: at least show the hero
      oldUrl; // else leave stale — mirrors the backend leaving uncovered slots untouched
  }
  const nextData = data.slice();
  nextData[idx] = { ...rec, art_bundle: { ...rec.art_bundle, data: nextBundle } };
  return { ...src, data: nextData };
};

/** Delete (archive) a regenerated version so it drops out of the family. The initial / live banner
 *  can't be deleted (the backend 400s; the editor also disables the action). Returns whether it was
 *  deleted. Direct service call, like {@link publishBanner}. */
export const deleteBannerVersion = async (
  tenantId: number,
  versionId: string,
  signal?: AbortSignal,
): Promise<boolean> => {
  const base = creativesServiceBase();
  if (!base) return false;
  const token = localStorage.getItem('access_token') || '';
  const config: AxiosRequestConfig = {
    signal,
    _owsNoAuthRecovery: true,
    validateStatus: (s) => s >= 200 && s < 300,
    ...(token ? { headers: { Authorization: token } } : {}),
  };
  try {
    await axios.delete(
      `${base}/creatives/tenant/${encodeURIComponent(String(tenantId))}/version/${encodeURIComponent(versionId)}`,
      config,
    );
    return true;
  } catch {
    return false;
  }
};

/** Recover an archived version back into the family. Returns whether it was restored. Direct service
 *  call, like {@link deleteBannerVersion}. */
export const restoreBannerVersion = async (
  tenantId: number,
  versionId: string,
  signal?: AbortSignal,
): Promise<boolean> => {
  const base = creativesServiceBase();
  if (!base) return false;
  const token = localStorage.getItem('access_token') || '';
  const config: AxiosRequestConfig = {
    signal,
    _owsNoAuthRecovery: true,
    validateStatus: (s) => s >= 200 && s < 300,
    ...(token ? { headers: { Authorization: token } } : {}),
  };
  try {
    await axios.post(
      `${base}/creatives/tenant/${encodeURIComponent(String(tenantId))}/version/${encodeURIComponent(versionId)}/restore`,
      {},
      config,
    );
    return true;
  } catch {
    return false;
  }
};

/** PERMANENTLY delete an ARCHIVED creative (irreversible hard delete), via the `?permanent=true` flag on the
 *  same version endpoint. The backend only purges a row that is already archived, so a live/initial version is
 *  never destroyed. Returns whether it was purged. Direct service call, like {@link deleteBannerVersion}. */
export const purgeBannerVersion = async (
  tenantId: number,
  versionId: string,
  signal?: AbortSignal,
): Promise<boolean> => {
  const base = creativesServiceBase();
  if (!base) return false;
  const token = localStorage.getItem('access_token') || '';
  const config: AxiosRequestConfig = {
    signal,
    _owsNoAuthRecovery: true,
    validateStatus: (s) => s >= 200 && s < 300,
    ...(token ? { headers: { Authorization: token } } : {}),
  };
  try {
    await axios.delete(
      `${base}/creatives/tenant/${encodeURIComponent(String(tenantId))}/version/${encodeURIComponent(versionId)}?permanent=true`,
      config,
    );
    return true;
  } catch {
    return false;
  }
};

/** Result of asking to generate a version's remaining formats. */
export interface GenerateFormatsResult {
  ok: boolean;
  jobId?: string;
  /** The version already had all its formats — nothing was charged. */
  complete?: boolean;
  needed?: number;
  balance?: number;
}

/**
 * Generate a MAIN-ONLY version's remaining formats (charged per remaining draw) as an async job that
 * streams per-format progress, tracked over the owner-hub websocket like a generation. Returns `{ ok:false, needed,
 * balance }` on a 402 so the caller can open Buy-credits, or `{ ok:true, complete:true }` when the
 * version already has every format. Direct service call, like {@link publishBanner}.
 */
export const generateBannerFormats = async (
  tenantId: number,
  rootBannerId: string,
  versionId: string,
  domain?: string,
  signal?: AbortSignal,
): Promise<GenerateFormatsResult> => {
  const base = creativesServiceBase();
  if (!base) return { ok: false };
  const token = localStorage.getItem('access_token') || '';
  const config: AxiosRequestConfig = {
    signal,
    _owsNoAuthRecovery: true,
    validateStatus: (s) => (s >= 200 && s < 300) || s === 402,
    ...(token ? { headers: { Authorization: token } } : {}),
  };
  // Pass the brand's own domain so the service can resolve the tenant's real site_id (the data-source
  // evaluate that enumerates the formats to draw 500s on site_id=0); mirrors the list read.
  const query = domain ? `?domain=${encodeURIComponent(domain)}` : '';
  const response = await axios.post<{
    ok?: boolean;
    job_id?: string;
    complete?: boolean;
    needed?: number;
    balance?: number;
  }>(
    `${base}/creatives/tenant/${encodeURIComponent(String(tenantId))}/banner/${encodeURIComponent(rootBannerId)}/version/${encodeURIComponent(versionId)}/formats${query}`,
    {},
    config,
  );
  if (response.status === 402) {
    return { ok: false, needed: response.data?.needed, balance: response.data?.balance };
  }
  return {
    ok: response.data?.ok !== false,
    jobId: response.data?.job_id,
    complete: response.data?.complete,
    needed: response.data?.needed,
    balance: response.data?.balance,
  };
};

/* Generation through the service is now ASYNC and owned by the `creativeJobs` store slice
 * (`enqueueGeneration` → 202 `{job_id}`, tracked over the owner-hub websocket; see `store/ws/ws.js`). The old
 * synchronous `generateCreativeViaService` helper was removed with the sync `{ok, creative}`
 * contract it spoke; see `src/store/slices/creativeJobs.ts`. */

/* ── 6. LIVE creatives via the logo family (owner-accessible today) ───────────
 *
 * The hero/promo banners a site renders are served through a PLAYER-scoped data source, so an
 * owner token can't read them (and this tenant has no owner-editable banner store yet). The one
 * creative family that IS owner-accessible right now, with real images AND a working edit
 * pipeline, is the brand's logo family: the marks that render across the site (top navigation,
 * footer, onboarding/emails, and every variant). So the In-Website tab reads THAT, and edits it
 * through the same launched-brand pipeline Settings › Logos uses. When the banner backend
 * (Contract B) ships, {@link fetchCreativeFamily} takes over and this becomes the fallback. */

/** How the logo slots group into the site placements an owner recognises. */
const LOGO_PLACEMENTS: ReadonlyArray<{ key: string; label: string; slots: string[] }> = [
  { key: 'top-navigation', label: 'Top navigation', slots: ['topNavDesktop', 'topNavMobile'] },
  { key: 'footer', label: 'Footer', slots: ['footer'] },
  { key: 'onboarding', label: 'Onboarding & emails', slots: ['onboarding'] },
  {
    key: 'brand-marks',
    label: 'Brand marks',
    slots: [
      'symbol_and_name_horizontal',
      'symbol_and_name_vertical',
      'name_horizontal',
      'symbol_and_short_name_horizontal',
      'name_horizontal_black',
      'name_horizontal_white',
      'symbol',
      'short_name',
    ],
  },
];

/** Turn the live logo family into the creatives view, grouped by site placement. Provenance
 *  values that name an owner upload mark a creative "Custom"; everything else is Lumio's work. */
export const logoFamilyToCreativeFamily = (family: LogoFamily): CreativeFamily => {
  const logos = family.logo_assets?.logos ?? {};
  const placements: CreativePlacement[] = [];
  for (const group of LOGO_PLACEMENTS) {
    const items: CreativeItem[] = [];
    for (const slot of group.slots) {
      const asset = logos[slot];
      if (!asset?.src) continue;
      const provenance = family.provenance?.[slot];
      const origin: CreativeOrigin = provenance && /upload|owner|custom/i.test(provenance) ? 'custom' : 'lumio';
      items.push({ banner_id: slot, image_url: asset.src, origin, internal_name: slot, layout: provenance });
    }
    if (items.length > 0) placements.push({ placement: group.key, label: group.label, items });
  }
  return { tenant_id: family.tenant_id, placements, source: 'logos' };
};

/** The brand's live logo family, mapped to the creatives view. Owner-accessible, real images. */
export const fetchLiveCreativeFamily = async (tenantId: number, signal?: AbortSignal): Promise<CreativeFamily> =>
  logoFamilyToCreativeFamily(await fetchLogoFamily(tenantId, signal));

/**
 * Replace one logo-family creative with the owner's own artwork, through the SAME launched-brand
 * pipeline Settings › Logos uses: store the image on spark-ai, then queue a casino-creation run
 * that seeds the slot from it (Lumio conditions it, `prepareUpload`; no cascade, so only the
 * picked slot changes). Returns the queued run's pipeline id to poll. Logos replace live (there
 * is no preview step), so the poller refetches the family when it settles.
 */
export const replaceLogoCreative = async (
  input: { tenantId: number; slot: string; imageDataUri: string; brandName: string },
  signal?: AbortSignal,
): Promise<{ pipelineId: string }> => {
  const uploadUrl = await uploadLogoArtwork(input.imageDataUri, input.brandName, signal);
  const run = await queueLogoRun(
    { tenantId: input.tenantId, slot: input.slot, uploadUrl, prepareUpload: true, cascade: false },
    signal,
  );
  return { pipelineId: run.pipelineId };
};

/** Poll a logo-family run (the replace pipeline reports terminal state the same way banners do). */
export const fetchLogoCreativeRunStatus = fetchLogoRunStatus;
